import React from 'react';
import { mapToStandardColor } from 'constants/colors';
import {
  ApplicationMethod,
  TimingEvent
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import {
  timingEventOptions,
  applicationMethodOptions
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/selectOptions';
import { Tag } from 'screens/Applications&WorkOrders/StepsLayout/Components/DetailsStep/components/TagsSection';

import { format } from 'date-fns';
import { formatDate } from 'screens/Applications&WorkOrders/Components/helpers';
import { OperationType } from 'screens/Applications&WorkOrders/RouteAdmin';
import RecordSourceButton from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab/components/RecordSourceButton';
import { RecordSource } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { ApplicationDetailsTabData } from '../hooks/useDetailsTabData';

export const formatEnumValue = <T extends Record<string, string | number>>(
  enumValue: T[keyof T] | undefined,
  enumObject: T,
  options: { value: string; label: string }[]
) => {
  if (!enumValue) return '---';

  const enumKey = Object.entries(enumObject).find(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([_, value]) => value === enumValue
  )?.[0];

  return options.find(option => option.value === enumKey)?.label;
};

const formatTags = (tags: Tag[]) => {
  if (tags.length === 0) return '---';

  return tags.map(tag => {
    const colorMapping = mapToStandardColor(tag.color);
    return {
      ...tag,
      textColor: colorMapping.text,
      backgroundColor: colorMapping.background
    };
  });
};

const formatDateByValue = (value: string, personName?: string) => {
  if (personName) {
    return `${formatDate(value)} by ${personName}`;
  }
  return value ? formatDate(value) : '---';
};

const formatArrayValues = (value: string[] | undefined) => {
  if (!Array.isArray(value) || value.length === 0) return '---';

  return value.join(', ');
};

const formatAuthorizationDate = (value: string, personName?: string) => {
  if (!value) return '---';

  const date = format(new Date(value), 'MMM d, yyyy');

  return personName ? `${date} by ${personName}` : date;
};

const formatRecordSource = (
  recordSource: RecordSource | undefined,
  type: OperationType
) => {
  if (!recordSource) {
    return '---';
  }
  return <RecordSourceButton recordSource={recordSource} type={type} />;
};

// eslint-disable-next-line import/prefer-default-export
export const formatApplicationDetailsTabContent = (
  data: ApplicationDetailsTabData,
  type: OperationType = OperationType.APPLICATION
) => {
  return [
    {
      key: 'authorization',
      label: 'Authorization',
      value: formatAuthorizationDate(
        data.authorizationDate,
        data.authorizedBy?.name
      )
    },
    {
      key: 'cropSeasons',
      label: 'Crop Season(s)',
      value: formatArrayValues(data?.cropSeasons)
    },
    {
      key: 'timingEvent',
      label: 'Timing Event',
      value: formatEnumValue(data.timingEvent, TimingEvent, timingEventOptions)
    },
    {
      key: 'reiExpirationDate',
      label:
        type === OperationType.APPLICATION
          ? 'REI Expiration'
          : 'REI Estimation',
      value: formatDateByValue(data.reiExpirationDate ?? ''),
      tooltip: 'Calculated by the most restrictive product'
    },
    {
      key: 'applicationMethod',
      label: 'Application Method',
      value: formatEnumValue(
        data.applicationMethod,
        ApplicationMethod,
        applicationMethodOptions
      )
    },
    {
      key: 'phiExpirationDate',
      label:
        type === OperationType.APPLICATION
          ? 'PHI Expiration'
          : 'PHI Estimation',
      value: formatDateByValue(data.phiExpirationDate ?? ''),
      tooltip: 'Calculated by the most restrictive product'
    },
    {
      key: 'equipments',
      label: 'Equipment',
      value: formatArrayValues(data?.equipments)
    },
    {
      key: 'id',
      label:
        type === OperationType.APPLICATION ? 'Application ID' : 'Work Order ID',
      value: data.name
    },
    {
      key: 'recordSource',
      label: 'Record Source',
      value: formatRecordSource(data.recordSource, type)
    },
    {
      key: 'createdBy',
      label: 'Record Created',
      value: formatDateByValue(data.createdAt, data.createdBy)
    },
    {
      key: 'tags',
      label: 'Tags',
      value: formatTags(data.tags)
    },
    {
      key: 'applicators',
      label: 'Applicators',
      value: data.applicators?.length ? data.applicators : '---'
    }
  ];
};
